import type { Control, FieldPath, FieldValues } from "react-hook-form";

import { Check, ChevronDown, Loader2 } from "lucide-react";
import { useState } from "react";

import { Button } from "@/components/ui/button";
import {
	Command,
	CommandEmpty,
	CommandGroup,
	CommandInput,
	CommandItem,
	CommandList
} from "@/components/ui/command";
import {
	FormControl,
	FormField,
	FormItem
} from "@/components/ui/form";
import { Label } from "@/components/ui/label";
import {
	Popover,
	PopoverContent,
	PopoverTrigger
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";

export interface SelectableItem {
	id: string;
	name: string;
}

interface ItemSearchProps<T extends FieldValues, I extends SelectableItem> {
	name: FieldPath<T>;
	control: Control<T>;
	items: I[];
	isLoading: boolean;
	label: string;
	placeholder?: string;
	icon?: React.ReactNode;
	searchPlaceholder?: string;
	emptyMessage?: string;
	disabled?: boolean;
}

export function ItemSearch<T extends FieldValues, I extends SelectableItem>({
	items,
	control,
	name,
	isLoading,
	label,
	placeholder = "Select...",
	icon,
	searchPlaceholder = "Search...",
	emptyMessage = "No items found.",
	disabled = false
}: ItemSearchProps<T, I>) {
	const [value, setValue] = useState("");
	const [open, setOpen] = useState(false);

	return (
		<FormField
			control={control}
			name={name}
			render={({ field }) => (
				<FormItem>
					<Label htmlFor={name} className="text-right">
						{label}
						<span className={cn("text-red-500", { "sr-only": !label })}> *</span>
					</Label>
					<FormControl>
						<Popover open={open} onOpenChange={setOpen} modal>
							<PopoverTrigger asChild>
								<Button
									variant="outline"
									className="w-full justify-between capitalize gap-2 h-12"
									disabled={disabled}
								>
									{icon && value && (
										<div className={cn("p-2 bg-gray-100 rounded-lg flex", { hidden: !value })}>
											{icon}
										</div>
									)}
									{value
										? (items.find(item => item.id === field.value)?.name)
										: placeholder}
									<ChevronDown className="h-4 w-4 shrink-0 opacity-50 ml-auto" />
								</Button>
							</PopoverTrigger>
							<PopoverContent className="w-[375px] p-0 overflow-auto">
								<Command>
									<CommandInput placeholder={searchPlaceholder} />
									<CommandEmpty>
										{isLoading
											? <Loader2 size={20} className="animate-spin opacity-50" />
											: emptyMessage}
									</CommandEmpty>

									<CommandList>
										<CommandGroup>
											{items.map(item => (
												<CommandItem
													key={item.id}
													onSelect={() => {
														field.onChange(item.id);
														setValue(item.name);
														setOpen(false);
													}}
													className="capitalize"
												>
													{item.name}
													<Check
														className={cn(
															"ml-auto h-4 w-4",
															field.value === item.id ? "opacity-100" : "opacity-0"
														)}
													/>
												</CommandItem>
											))}
										</CommandGroup>
									</CommandList>
								</Command>
							</PopoverContent>
						</Popover>
					</FormControl>
				</FormItem>
			)}
		/>
	);
}
